import React from 'react';
import { v4 as uuid } from 'uuid';
import { createEvent } from '../../../api/events/client';
import { upload, uploadPresignedUrl } from '../../../api/files/client';
import type { DisplayMessage } from '../chatHelpers';

interface Result {
  pendingFileForUpload: File | null;
  setPendingFileForUpload: React.Dispatch<React.SetStateAction<File | null>>;
  fileTitle: string;
  setFileTitle: React.Dispatch<React.SetStateAction<string>>;
  fileUploading: boolean;
  dragging: boolean;
  fileInputRef: React.RefObject<HTMLInputElement>;
  triggerFileSelect: () => void;
  handleConfirmUpload: () => Promise<void>;
  cancelUpload: () => void;
  handleDragEnter: (e: React.DragEvent) => void;
  handleDragLeave: (e: React.DragEvent) => void;
  handleDragOver: (e: React.DragEvent) => void;
  handleDrop: (e: React.DragEvent) => void;
}

// Owns the file-upload state machine (drag overlay → picker / drop → confirm
// card → S3 upload + createFile + createEvent). Notifies the chat by pushing a
// SYSTEM message into `setDisplayMessages` once the upload completes.
export function useFileUpload(
  dossierId: string,
  setDisplayMessages: React.Dispatch<React.SetStateAction<DisplayMessage[]>>
): Result {
  const [pendingFileForUpload, setPendingFileForUpload] =
    React.useState<File | null>(null);
  const [fileTitle, setFileTitle] = React.useState('');
  const [fileUploading, setFileUploading] = React.useState(false);
  const [dragging, setDragging] = React.useState(false);
  const dragCounterRef = React.useRef(0);
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  const triggerFileSelect = React.useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const cancelUpload = React.useCallback(() => {
    setPendingFileForUpload(null);
    setFileTitle('');
  }, []);

  const handleConfirmUpload = React.useCallback(async () => {
    if (!dossierId || !pendingFileForUpload || fileUploading) {
      return;
    }
    const file = pendingFileForUpload;
    const title = fileTitle.trim() || file.name.replace(/\.[^.]+$/, '');
    setFileUploading(true);
    try {
      const uploadId = uuid();
      const contentType = file.type || 'application/octet-stream';
      const presigned = await uploadPresignedUrl({
        fileName: file.name,
        contentType,
        uploadId,
      });
      await upload({ ...presigned, contentType, file });
      const presignedUrl = new URL(presigned.url);
      const s3Bucket = presignedUrl.hostname.split('.')[0];
      const s3Key = presignedUrl.pathname.slice(1).split('?')[0];
      const { file_id: createdFileId } = await createEvent(dossierId, {
        event_type: 'file_received',
        event_date: new Date().toISOString(),
        file_name: file.name,
        file_type: 'DOC',
        s3_key: s3Key,
        s3_bucket: s3Bucket,
        title,
      });
      setDisplayMessages((prev) => [
        ...prev,
        {
          message_id: `upload-${createdFileId}`,
          role: 'SYSTEM' as const,
          text: title,
          created_at: new Date().toISOString(),
        },
      ]);
      setPendingFileForUpload(null);
      setFileTitle('');
    } finally {
      setFileUploading(false);
    }
  }, [
    dossierId,
    pendingFileForUpload,
    fileTitle,
    fileUploading,
    setDisplayMessages,
  ]);

  const handleDragEnter = React.useCallback((e: React.DragEvent) => {
    e.preventDefault();
    dragCounterRef.current += 1;
    if (dragCounterRef.current === 1) {
      setDragging(true);
    }
  }, []);

  const handleDragLeave = React.useCallback((e: React.DragEvent) => {
    e.preventDefault();
    dragCounterRef.current -= 1;
    if (dragCounterRef.current === 0) {
      setDragging(false);
    }
  }, []);

  const handleDragOver = React.useCallback((e: React.DragEvent) => {
    e.preventDefault();
  }, []);

  const handleDrop = React.useCallback((e: React.DragEvent) => {
    e.preventDefault();
    dragCounterRef.current = 0;
    setDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file) {
      setPendingFileForUpload(file);
      setFileTitle(file.name.replace(/\.[^.]+$/, ''));
    }
  }, []);

  return {
    pendingFileForUpload,
    setPendingFileForUpload,
    fileTitle,
    setFileTitle,
    fileUploading,
    dragging,
    fileInputRef,
    triggerFileSelect,
    handleConfirmUpload,
    cancelUpload,
    handleDragEnter,
    handleDragLeave,
    handleDragOver,
    handleDrop,
  };
}
